import * as z from 'zod';

import {imageFromSanityImage} from '@/modules/foundation/sanity/image/query';
import type {CommerceBrand} from '@/sanity/schemas/types';

import type {ProductFilters} from '../../filters/query';
import {intersectMatchesAndFilters} from '../../filters/utils';
import type {ItemWithVatTypeFragment} from '../../item/query';
import type {ProductAdapter} from '../adapter';
import type {ProductDescription} from '../description';
import type {ProductImageSide} from '../ProductImage';
import type {ProductImageStackDescription} from '../ProductImageStack';
import type {UnionProductFragment} from '../query';

import {DOCUMENT_TYPE_PRODUCT} from './schema/names';
import type {ProductFragment} from './query';

export function isProduct(product: UnionProductFragment): product is ProductFragment {
	return product._type === DOCUMENT_TYPE_PRODUCT;
}

export const productConfigurationSchema = z.object({
	variantId: z.string().nullable(),
});

export type ProductConfiguration = z.infer<typeof productConfigurationSchema>;

export function parseProductConfiguration(value: unknown) {
	const result = productConfigurationSchema.safeParse(value);
	if (!result.success) {
		console.warn('Failed to parse product configuration', result.error);
		return null;
	}

	return result.data;
}

export function getDefaultProductConfiguration(product: ProductFragment): ProductConfiguration {
	const variantId = getFirstVariantKey(product);

	return {
		variantId,
	};
}

export function getFirstVariantKey(product: ProductFragment) {
	if (product.variants?.length) {
		return product.variants[0]._key;
	}

	return null;
}

export function productConfigurationFromFormData(
	product: ProductFragment,
	formData: FormData,
): ProductConfiguration | null {
	// FIXME: Should validate ID here?
	const variantId = formData.get('variantId')?.toString() ?? null;

	return {
		variantId,
	};
}

export function isValidProductConfiguration(
	product: ProductFragment,
	configuration: ProductConfiguration,
) {
	// Check if the variantId is valid:
	const selectedVariant = product.variants?.find(
		(variant) => variant._key === configuration.variantId,
	);
	if (!selectedVariant) {
		return false;
	}

	return true;
}

export function getItemsForProductConfiguration(
	product: ProductFragment,
	configuration: ProductConfiguration,
) {
	const selectedVariant = product.variants?.find((v) => v._key === configuration.variantId);
	const selectedItems: ItemWithVatTypeFragment[] = [];

	if (selectedVariant?.items?.length) {
		selectedItems.push(...selectedVariant.items);
	}

	return selectedItems;
}

export function getProductNameForProductConfiguration(
	product: ProductFragment,
	_configuration: ProductConfiguration,
) {
	return `${product.name}`;
}

export function getDescriptionForProductConfiguration(
	product: ProductFragment,
	configuration: ProductConfiguration,
) {
	const desc: ProductDescription = [];

	const variant = product.variants?.find((variant) => variant._key === configuration.variantId);

	if (!variant || !variant.name) {
		return null;
	} else {
		desc.push({title: 'Variant', value: variant.name});
	}

	return desc;
}

export function getImageStackForProductConfiguration<Side extends ProductImageSide>(
	side: Side,
	product: ProductFragment,
	config: ProductConfiguration | null,
): ProductImageStackDescription | null {
	let selectedVariant = null;
	if (config && product.variants) {
		selectedVariant = product.variants.find((v) => v._key === config.variantId);
	}

	let image = imageFromSanityImage(product.images?.[side]);
	if (!image) {
		// We only consider this side if there is a base image for it:
		return null;
	}

	if (selectedVariant) {
		const img = imageFromSanityImage(selectedVariant.images?.[side]);
		if (img) {
			image = img;
		}
	}

	return {
		image,
		altText: '',
		overlays: [],
	};
}

export const getAllProductBrands = (product: ProductFragment) => {
	const brandsById = new Map<string, CommerceBrand>();

	// Add the base product brands:
	for (const brand of getBaseProductBrands(product)) {
		brandsById.set(brand._id, brand);
	}

	return Array.from(brandsById.values());
};

export const getConfiguredProductBrands = (
	product: ProductFragment,
	_configuration: ProductConfiguration,
) => {
	const brandsById = new Map<string, CommerceBrand>();

	// Add the base product brands:
	for (const brand of getBaseProductBrands(product)) {
		brandsById.set(brand._id, brand);
	}

	return Array.from(brandsById.values());
};

const getBaseProductBrands = (product: ProductFragment) => {
	const brandsById = new Map<string, CommerceBrand>();

	// add the main prduct brand:
	for (const brand of product.brands ?? []) {
		brandsById.set(brand._id, brand);
	}

	return Array.from(brandsById.values());
};

export function getFiltersMatchingProductConfiguration(
	product: ProductFragment,
	filters: ProductFilters,
): ProductFilters | null {
	const matches = {
		brandIds: getAllProductBrands(product).map((b) => b._id),
	} satisfies ProductFilters;

	return intersectMatchesAndFilters(matches, filters);
}

export const productAdapter: ProductAdapter<ProductFragment> = {
	getName: getProductNameForProductConfiguration,
	getItems: getItemsForProductConfiguration,
	getImages: getImageStackForProductConfiguration,
	getDescription: getDescriptionForProductConfiguration,
	isValidConfig: isValidProductConfiguration,
	getMatchingFilters: getFiltersMatchingProductConfiguration,
	getDefaultConfig: getDefaultProductConfiguration,
	parseConfigFromFormData: productConfigurationFromFormData,
	parseConfigFromJSON: parseProductConfiguration,
};
